import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Button } from '@xarvio/button';
import { Column, ColumnCell, Panel, Separator } from '@xarvio/layout';
import { SubmenuItem, SubmenuItemProps } from './SubmenuItem';
import { SubmenuStateContext } from './types';
import styles from './Submenu.module.scss';

interface SubmenuProps {
  isCollapsed?: boolean;
  isCollapsible?: boolean;
  onCollapsed?: () => void;
  titleIconEl: SubmenuItemProps['iconEl'];
  titleLabelEl: SubmenuItemProps['titleEl'];
  bottomElement?: React.ReactNode;
  stickyChildren?: React.ReactNode;
}

const Submenu: React.FC<React.PropsWithChildren<SubmenuProps>> = ({
  isCollapsed = false,
  isCollapsible = true,
  onCollapsed,
  titleIconEl,
  titleLabelEl,
  bottomElement,
  stickyChildren,
  children,
}) => {
  // height calculation, appending ellipsis button if content doesn't fit
  // there should be no scrollbar on collapsed state
  const [isEllipsisNeeded, setIsEllipsisNeeded] = useState<boolean>(false);
  const totalHeightRef = useRef<HTMLDivElement>(null);
  const contentHeightRef = useRef<HTMLDivElement>(null);

  const checkHeight = () => {
    if (totalHeightRef.current && contentHeightRef.current) {
      if (
        contentHeightRef.current.clientHeight >
        totalHeightRef.current.clientHeight
      ) {
        setIsEllipsisNeeded(true);
        // scroll panel to top
        totalHeightRef.current.scrollIntoView?.();
      }
    }
  };

  // check on first render
  useEffect(() => {
    if (isCollapsed) {
      checkHeight();
    }
  }, [isCollapsed]);

  return (
    <SubmenuStateContext.Provider value={{ isCollapsed }}>
      <Panel
        size={'s'}
        active
        className={cx(styles.panel, {
          [styles.panel_collapsed]: isCollapsed,
          [styles.panel_withEllipsis]: isEllipsisNeeded,
        })}
      >
        <Column>
          <ColumnCell>
            <>
              {/* TITLE */}
              <div className={styles.title}>
                <SubmenuItem
                  titleEl={titleLabelEl}
                  iconEl={titleIconEl}
                  withTooltip={false}
                  sideEl={
                    isCollapsible && (
                      <Button
                        type={'primary'}
                        blur
                        size={'small'}
                        iconName={
                          isCollapsed ? 'sidebar-right' : 'sidebar-left'
                        }
                        onClick={onCollapsed}
                        seleniumAttribute="collapse-panel-button"
                      />
                    )
                  }
                />
              </div>
              {stickyChildren}
            </>
          </ColumnCell>
          <ColumnCell max scrolling className={styles.scrollingColumnCell}>
            <div className={styles.totalHeight} ref={totalHeightRef}>
              <div className={styles.overflowContainer} ref={contentHeightRef}>
                {children}
              </div>
              <div className={styles.overflowButton}>
                <Button
                  type={'primary'}
                  blur
                  size={'small'}
                  iconName={'ellipsis'}
                  onClick={onCollapsed}
                />
              </div>
            </div>
          </ColumnCell>
          {bottomElement && (
            <ColumnCell>
              <Separator />
              {bottomElement}
            </ColumnCell>
          )}
        </Column>
      </Panel>
    </SubmenuStateContext.Provider>
  );
};

export { Submenu };
